import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Mail, Plus, Search, Trash2, BarChart3, Archive,
  ClipboardCheck, Send, Clock, FileEdit, AlertCircle, Info, MailPlus, ExternalLink,
} from 'lucide-react';
import {
  listCampaigns, deleteCampaign, updateCampaign,
  supabase, type Campaign, type CampaignStats,
} from '../../lib/supabase';
import { FreigabeBadge } from '../../components/Freigabe';
import { istRedaktion, istTeam } from '../../lib/rollen';
import { brevoKampagneUrl } from '../../lib/brevo';
import { roleLabel } from '../../lib/format';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { formatDateTime, formatNumber } from '../../lib/format';
import { useAuthStore } from '../../stores/authStore';

const STATUS_FILTERS: { value: Campaign['status'] | 'all'; label: string; icon?: typeof Clock }[] = [
  { value: 'all', label: 'Alle' },
  { value: 'draft', label: 'Entwürfe', icon: FileEdit },
  { value: 'pending_approval', label: 'Warten auf Freigabe', icon: Clock },
  { value: 'scheduled', label: 'Geplant' },
  { value: 'sending', label: 'Wird gesendet' },
  { value: 'sent', label: 'Versendet', icon: Send },
  { value: 'failed', label: 'Fehler' },
  { value: 'archived', label: 'Archiviert' },
];

function pct(numerator: number, denominator: number): string {
  if (!denominator) return '0%';
  const p = (numerator / denominator) * 100;
  if (p < 1 && p > 0) return p.toFixed(2).replace('.', ',') + '%';
  return p.toFixed(p < 10 ? 2 : 0).replace('.', ',') + '%';
}

function Metric({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div style={{ minWidth: 80, textAlign: 'right' }}>
      <div style={{ fontSize: '0.6875rem', color: 'var(--text-tertiary)', fontWeight: 500, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.6875rem', color: 'var(--text-tertiary)', marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

export function CampaignList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const canEdit = user?.role === 'admin' || user?.role === 'redakteur';
  const isAdmin = user?.role === 'admin';
  const redaktion = istRedaktion(user);
  // Eingereichte Mails entscheidet die Redaktion unter „Freigaben" — dort
  // stehen Vorab-Antworten, Wunschtermin und der Verlauf.
  // Mails aus dem Assistenten öffnen im Assistenten (Entwurf) bzw. unter
  // „Freigaben" — der Kampagnen-Editor zeigt sie nur an.
  const ziel = (c: Campaign) => {
    if (redaktion && (c.status === 'pending_approval' || (c.requires_approval && c.review_state === 'rueckfrage'))) return `/freigaben?mail=${c.id}`;
    if (c.builder_template && c.status === 'draft' && (c.created_by === user?.id || isAdmin)) return `/neue-mail/${c.id}`;
    return `/campaigns/${c.id}`;
  };

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Campaign['status'] | 'all'>('all');

  const { data: campaigns, isLoading } = useQuery({
    queryKey: ['campaigns'],
    queryFn: listCampaigns,
    // Solange noch eine Kampagne 'wird gesendet', alle 4s nachschauen — dann
    // wechselt der Badge automatisch auf 'versendet' bzw. 'fehler', sobald der
    // Background-Task im Edge Function fertig ist.
    refetchInterval: (query) => {
      const data = query.state.data as Campaign[] | undefined;
      const hasSending = data?.some(c => c.status === 'sending');
      return hasSending ? 4000 : false;
    },
  });

  // Stats für alle Kampagnen — ein Query statt N
  const { data: statsMap } = useQuery({
    queryKey: ['all-campaign-stats'],
    queryFn: async (): Promise<Record<string, CampaignStats>> => {
      if (!supabase) return {};
      const { data } = await supabase.from('Mail_campaign_stats').select('*');
      const map: Record<string, CampaignStats> = {};
      for (const row of (data ?? []) as CampaignStats[]) {
        map[row.campaign_id] = row;
      }
      return map;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCampaign,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => updateCampaign(id, { status: 'archived' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
  });

  const handleDelete = (c: Campaign) => {
    if (!confirm(`Kampagne "${c.name}" wirklich löschen?`)) return;
    deleteMutation.mutate(c.id);
  };
  const handleArchive = (c: Campaign) => {
    if (!confirm(`Kampagne "${c.name}" archivieren?`)) return;
    archiveMutation.mutate(c.id);
  };
  const filtered = useMemo(() => (campaigns ?? []).filter(c => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (search.trim() && !(c.name + c.subject).toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [campaigns, statusFilter, search]);

  // Anzahl pro Status für die Filter-Badges
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of (campaigns ?? [])) {
      counts[c.status] = (counts[c.status] ?? 0) + 1;
    }
    return counts;
  }, [campaigns]);

  const pendingCount = statusCounts['pending_approval'] ?? 0;

  return (
    <div>
      <PageHeader
        title="Kampagnen"
        subtitle="Alle E-Mail-Kampagnen der Randori-Pro"
        actions={canEdit && (
          <>
            <Link to="/neue-mail" className="btn btn-secondary"><MailPlus size={16} /> Neue Mail (Assistent)</Link>
            <Link to="/campaigns/new" className="btn btn-primary"><Plus size={16} /> Neue Kampagne</Link>
          </>
        )}
      />

      <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
        <Info size={16} />
        <div>
          <strong>Was Du hier machen kannst:</strong> Kampagnen anlegen, Empfänger über Gruppen wählen,
          HTML-Vorlagen verwenden und einen Test-Versand starten. Für kurze Mails im Haus-Layout ist der Assistent („Neue Mail") schneller.
          {!isAdmin && ' Vor dem finalen Versand reichst Du die Kampagne zur Freigabe ein – die Redaktion wird per E-Mail informiert.'}
          {' '}Mails von Geschäftsführung und Studioleitung entscheidet die Redaktion unter „Freigaben".
        </div>
      </div>

      {redaktion && pendingCount > 0 && (
        <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
          <Clock size={16} />
          <div>
            <strong>{pendingCount} Kampagne{pendingCount === 1 ? '' : 'n'}</strong> {pendingCount === 1 ? 'wartet' : 'warten'} auf die Freigabe.
            {' '}
            <Link to="/freigaben" className="btn btn-ghost btn-sm" style={{ padding: '0.15rem 0.5rem' }}>
              Zu den Freigaben →
            </Link>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="search-input-wrapper">
          <Search size={16} />
          <input type="text" className="search-input" placeholder="Suche nach Name oder Betreff..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ maxWidth: '220px' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as Campaign['status'] | 'all')}>
          {STATUS_FILTERS.map(f => {
            const count = f.value === 'all' ? (campaigns?.length ?? 0) : (statusCounts[f.value] ?? 0);
            return (<option key={f.value} value={f.value}>{f.label}{count ? ` (${count})` : ''}</option>);
          })}
        </select>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><div className="loading-spinner" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Mail}
          title={campaigns?.length === 0 ? 'Noch keine Kampagnen' : 'Keine Treffer'}
          description={campaigns?.length === 0 ? 'Legen Sie Ihre erste Kampagne an, um loszulegen.' : 'Versuchen Sie andere Filter oder Suchbegriffe.'}
          action={canEdit && campaigns?.length === 0 ? (<Link to="/campaigns/new" className="btn btn-primary"><Plus size={16} /> Erste Kampagne anlegen</Link>) : undefined}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map((c, idx) => {
            const stats = statsMap?.[c.id];
            const sent = stats?.sent_count ?? 0;
            const delivered = stats?.delivered_count ?? sent;
            const opens = stats?.unique_opens ?? 0;
            const clicks = stats?.unique_clicks ?? 0;
            const unsub = stats?.unsubscribed ?? 0;
            const hasStats = c.status === 'sent' && sent > 0;
            const isPending = c.status === 'pending_approval';

            return (
              <div
                key={c.id}
                onClick={() => navigate(ziel(c))}
                style={{
                  background: 'var(--surface-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '1rem 1.25rem',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1.5rem',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--primary)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Linke Spalte: Name, Status, Infos */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {c.name}
                    </h3>
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '0.375rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {c.subject || <em style={{ color: 'var(--text-tertiary)' }}>(kein Betreff)</em>}
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', fontSize: '0.75rem', color: 'var(--text-tertiary)', flexWrap: 'wrap' }}>
                    <FreigabeBadge campaign={c} />
                    {c.sent_at && <span>Gesendet am {formatDateTime(c.sent_at)}</span>}
                    {!c.sent_at && c.scheduled_at && <span>Geplant für {formatDateTime(c.scheduled_at)}</span>}
                    {!c.sent_at && !c.scheduled_at && c.status === 'pending_approval' && c.submitted_for_approval_at && (
                      <span>Eingereicht am {formatDateTime(c.submitted_for_approval_at)}</span>
                    )}
                    {!c.sent_at && !c.scheduled_at && c.status === 'draft' && (
                      <span>Geändert {formatDateTime(c.updated_at)}</span>
                    )}
                    {c.requires_approval && c.created_by_name && (
                      <span>von {c.created_by_name}{c.created_by_role ? ` (${roleLabel(c.created_by_role)})` : ''}{c.target_studios?.length ? ` · ${c.target_studios.join(', ')}` : ''}</span>
                    )}
                    {c.requires_approval && !c.sent_at && !c.scheduled_at && c.desired_send_at && <span>Wunsch {formatDateTime(c.desired_send_at)}</span>}
                    {c.is_urgent && !c.sent_at && <span className="badge badge-danger">Eilt</span>}
                    <span style={{ opacity: 0.6 }}>#{(filtered.length - idx).toString().padStart(2, '0')}</span>
                  </div>
                  {c.rejection_reason && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--danger-dark)', marginTop: '0.375rem' }}>
                      <AlertCircle size={12} style={{ display: 'inline', marginRight: 4 }} />
                      {c.review_state === 'rueckfrage' ? 'Rückfrage' : 'Abgelehnt'}: {c.rejection_reason}
                    </div>
                  )}
                </div>

                {/* Mitte: Stats (wenn vorhanden) */}
                {hasStats && (
                  <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', borderLeft: '1px solid var(--border-light)', paddingLeft: '1.5rem' }}>
                    <Metric label="Empfänger:innen" value={formatNumber(sent)} sub="100%" />
                    <Metric label="Öffnungen" value={formatNumber(opens)} sub={pct(opens, delivered)} />
                    <Metric label="Klicks" value={formatNumber(clicks)} sub={pct(clicks, delivered)} />
                    <Metric label="Abgemeldet" value={formatNumber(unsub)} sub={pct(unsub, delivered)} />
                  </div>
                )}

                {/* Rechts: Aktionen */}
                <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                  {isPending && redaktion && (
                    <Link to={`/freigaben?mail=${c.id}`} className="btn btn-primary btn-sm" title="Prüfen und freigeben">
                      <ClipboardCheck size={14} /> Prüfen
                    </Link>
                  )}
                  {c.brevo_campaign_id && istTeam(user) && (
                    <a
                      href={brevoKampagneUrl(c.brevo_campaign_id, c.status) ?? undefined}
                      target="_blank" rel="noopener noreferrer"
                      className="btn btn-ghost btn-sm"
                      title="Diese Kampagne direkt in Brevo öffnen (neuer Tab)"
                      aria-label={`${c.name} in Brevo öffnen`}
                    >
                      <ExternalLink size={14} />
                    </a>
                  )}
                  {c.status === 'sent' && (
                    <Link to={`/campaigns/${c.id}/stats`} className="btn btn-ghost btn-sm" title="Detail-Statistik">
                      <BarChart3 size={14} />
                    </Link>
                  )}
                  {canEdit && !isPending && c.status !== 'archived' && c.status !== 'sent' && c.status !== 'sending' && (
                    <button className="btn btn-ghost btn-sm" onClick={() => handleArchive(c)} title="Archivieren">
                      <Archive size={14} />
                    </button>
                  )}
                  {isAdmin && (
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(c)} title="Löschen">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
